import { useEffect, useState } from "react";
import { doc, onSnapshot, Firestore } from "firebase/firestore";
import { db } from "../config/firebase";
import { RoundSolve } from "../components/utils/MatchUtils";


export const formatTime = (time: number): string => {
  const milliseconds = Math.floor(time % 1000);
  const seconds = Math.floor((time / 1000) % 60);
  const minutes = Math.floor((time / 60000) % 60);
  if(minutes > 0){
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${milliseconds.toString().padStart(3, '0')}`;
    }
    
    return `${seconds.toString().padStart(1, '0')}.${milliseconds.toString().padStart(3, '0')}`;
  };

interface UseSolveResult {
  solve: RoundSolve | null;
  loadingSolve: boolean;
  errorSolve: Error | null;
}

/**
 * Custom hook to listen for real-time updates for a specific solve for a user in a round.
 *
 * @param matchId - The ID of the match document
 * @param playerId - The ID of the player
 * @param roundNumber - The round number
 * @param solveNumber - The solve number within the round
 * @returns The real-time solve data, loading state, and error (if any)
 */
export const useSolve = (
  matchId: string,
  playerId: string,
  roundNumber: number,
  solveNumber: number
): UseSolveResult => {
  const [solve, setSolve] = useState<RoundSolve | null>(null);
  const [loadingSolve, setLoading] = useState<boolean>(true);
  const [errorSolve, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!matchId || !playerId || roundNumber < 0 || solveNumber < 0) {
      setError(new Error("Invalid parameters provided."));
      setLoading(false);
      return;
    }

    const solveId = `round${roundNumber}-solve${solveNumber}`;
    const solveRef = doc(
      db,
      "matches",
      matchId,
      "players",
      playerId,
      "round-solves",
      solveId
    );

    const unsubscribe = onSnapshot(
      solveRef,
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          setSolve(docSnapshot.data() as RoundSolve);
        } else {
          setSolve(null);
        }
        setLoading(false);
      },
      (error) => {
        setError(error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [db, matchId, playerId, roundNumber, solveNumber]);

  return { solve, loadingSolve: loadingSolve, errorSolve: errorSolve };
};
